/**
 * gen90-subscription-buybox.js  (v2 — two-level selection)
 *
 * Behavior for snippets/gen90-subscription-buybox.liquid. Fully self-contained:
 * no dependency on theme.js, theme.Product, or theme.Variants.
 *
 * FROZEN from v1 (do not change without re-running the QA checklist in
 * docs/gen90-buybox-contract.md): the /cart/add.js JSON POST; the bubbling
 * `cart:build` + `ajaxProduct:added` dispatch that opens the theme's native
 * #CartDrawer; the HTTP-422 / network error handling; the page-level and
 * per-root idempotency guards; the AbortController teardown; and the
 * shopify:section:load / :unload re-init. These are unchanged.
 *
 * REWRITTEN in v2: only the SELECTION LAYER. The buy box is now a two-level
 * model — a `plan` radio group ([data-gen90-plan], sub|onetime) plus a nested
 * `freq` radio group ([data-gen90-freq], 30|60|90). Card and chip highlight states
 * are pure CSS (:has()); the JS only (a) mirrors the selected frequency's price
 * and "Save N%" into the sub-card text nodes, and (b) resolves the active
 * price + selling-plan id for the POST. All arithmetic was pre-computed in
 * Liquid and stored as data-* attributes, so this file does no math.
 *
 * Data contract (see the snippet):
 *   root:            [data-gen90-buybox][data-gen90-variant-id]
 *   plan radios:     [data-gen90-plan] value="sub" | value="onetime"
 *                    onetime also carries [data-gen90-price] + [data-gen90-selling-plan=""]
 *   freq radios:     [data-gen90-freq][data-gen90-selling-plan][data-gen90-price][data-gen90-save]
 *   reactive nodes:  [data-gen90-sub-price] [data-gen90-sub-save]
 *   other:           [data-gen90-quantity] [data-gen90-error] [data-gen90-add-to-cart]
 */
(function () {
  'use strict';

  // FROZEN — one-time page-level setup guard (multi-instance pages emit this
  // <script src> more than once; only the first execution registers the
  // document-level listeners, later ones just wire up new roots).
  if (window.LPBuybox && window.LPBuybox.loaded) {
    window.LPBuybox.init();
    return;
  }

  // FROZEN
  function getErrorMessage(data) {
    if (data && typeof data.description === 'string') {
      return data.description;
    }
    if (data && typeof data.message === 'string') {
      return data.message;
    }
    return 'Sorry, that item could not be added to your cart. Please try again.';
  }

  function checkedIn(list) {
    for (var i = 0; i < list.length; i++) {
      if (list[i].checked) {
        return list[i];
      }
    }
    return null;
  }

  function initBuybox(root) {
    // FROZEN — per-root idempotency guard.
    if (root.getAttribute('data-gen90-initialized') === 'true') {
      return;
    }

    var variantId = root.getAttribute('data-gen90-variant-id');
    var planRadios = root.querySelectorAll('[data-gen90-plan]');
    var freqRadios = root.querySelectorAll('[data-gen90-freq]');
    var subPriceEl = root.querySelector('[data-gen90-sub-price]');
    var subSaveEl = root.querySelector('[data-gen90-sub-save]');
    var quantityInput = root.querySelector('[data-gen90-quantity]');
    var errorEl = root.querySelector('[data-gen90-error]');
    var submitBtn = root.querySelector('[data-gen90-add-to-cart]');

    if (!submitBtn || !variantId || !planRadios.length) {
      return;
    }

    root.setAttribute('data-gen90-initialized', 'true');

    // FROZEN — AbortController teardown for shopify:section:unload.
    var controller = window.AbortController ? new AbortController() : null;
    var listenerOptions = controller ? { signal: controller.signal } : false;
    root.__lpAbortController = controller;

    var defaultLabel = submitBtn.textContent.trim();

    // ===== REWRITTEN: selection layer =====

    // Resolve the active price + selling-plan for the POST. Two-level:
    // sub  -> the checked frequency chip's plan; onetime -> no selling_plan.
    function activeSelection() {
      var plan = checkedIn(planRadios);
      if (!plan) {
        return null;
      }
      if (plan.value === 'sub') {
        var freq = checkedIn(freqRadios);
        if (!freq) {
          return null;
        }
        return {
          sellingPlan: freq.getAttribute('data-gen90-selling-plan') || '',
          price: freq.getAttribute('data-gen90-price') || ''
        };
      }
      return {
        sellingPlan: plan.getAttribute('data-gen90-selling-plan') || '',
        price: plan.getAttribute('data-gen90-price') || ''
      };
    }

    // Mirror the selected frequency's price + savings into the sub-card. The
    // sub card always reflects the chosen frequency (independent of whether
    // sub or one-time is the active plan), matching the approved comp.
    function updateSubCard() {
      var freq = checkedIn(freqRadios);
      if (!freq) {
        return;
      }
      if (subPriceEl) {
        subPriceEl.textContent = freq.getAttribute('data-gen90-price') || '';
      }
      if (subSaveEl) {
        subSaveEl.textContent = freq.getAttribute('data-gen90-save') || '';
      }
    }

    // Choosing a delivery schedule = choosing a subscription: selecting any
    // frequency chip switches the plan radio to "sub". They are separate radio
    // groups, so this is wired in JS. Then mirror the chosen price/savings.
    var subPlanRadio = root.querySelector('[data-gen90-plan][value="sub"]');
    function onFreqChange() {
      if (subPlanRadio) {
        subPlanRadio.checked = true;
      }
      updateSubCard();
    }
    for (var i = 0; i < freqRadios.length; i++) {
      freqRadios[i].addEventListener('change', onFreqChange, listenerOptions);
    }

    // ===== FROZEN: display/error helpers + cart POST =====

    function clearError() {
      if (errorEl) {
        errorEl.hidden = true;
        errorEl.textContent = '';
      }
    }

    function showError(message) {
      if (errorEl) {
        errorEl.hidden = false;
        errorEl.textContent = message;
      }
    }

    function setLoading(isLoading) {
      submitBtn.disabled = isLoading;
      submitBtn.textContent = isLoading ? 'Adding…' : defaultLabel;
    }

    submitBtn.addEventListener(
      'click',
      function () {
        clearError();

        var sel = activeSelection();
        if (!sel) {
          showError('Please select a purchase option.');
          return;
        }

        var quantity = parseInt((quantityInput && quantityInput.value) || '1', 10);
        if (!quantity || quantity < 1) {
          quantity = 1;
        }

        var payload = {
          id: variantId,
          quantity: quantity
        };
        if (sel.sellingPlan) {
          payload.selling_plan = sel.sellingPlan;
        }

        setLoading(true);

        fetch('/cart/add.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(payload)
        })
          .then(function (response) {
            return response.json().then(function (data) {
              return { ok: response.ok, data: data };
            });
          })
          .then(function (result) {
            setLoading(false);

            if (!result.ok || result.data.status) {
              showError(getErrorMessage(result.data));
              return;
            }

            document.dispatchEvent(new CustomEvent('cart:build', { bubbles: true }));
            document.dispatchEvent(
              new CustomEvent('ajaxProduct:added', {
                detail: {
                  product: result.data,
                  addToCartBtn: submitBtn
                },
                bubbles: true
              })
            );
          })
          .catch(function () {
            setLoading(false);
            showError('There was a problem adding this to your cart. Please try again.');
          });
      },
      listenerOptions
    );

    // Populate the sub-card from the default-checked frequency on init.
    updateSubCard();
  }

  // FROZEN
  function teardownBuybox(root) {
    if (root.__lpAbortController) {
      root.__lpAbortController.abort();
      root.__lpAbortController = null;
    }
    root.removeAttribute('data-gen90-initialized');
  }

  // FROZEN
  function init(scope) {
    var context = scope && scope.querySelectorAll ? scope : document;
    var roots = context.querySelectorAll('[data-gen90-buybox]');
    roots.forEach(initBuybox);
  }

  window.LPBuybox = { loaded: true, init: init };

  // FROZEN
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init();
    });
  } else {
    init();
  }

  // FROZEN — theme editor re-render: script tags in re-rendered HTML don't
  // execute, so re-running init() from this once-registered listener keeps the
  // buy box working after an edit. init() is scoped to event.target and no-ops
  // roots it already wired up.
  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });

  // FROZEN — tear down any buy box roots in a section about to be replaced.
  document.addEventListener('shopify:section:unload', function (event) {
    var target = event.target;
    if (!target || !target.querySelectorAll) {
      return;
    }
    if (target.matches && target.matches('[data-gen90-buybox]')) {
      teardownBuybox(target);
    }
    target.querySelectorAll('[data-gen90-buybox]').forEach(teardownBuybox);
  });
})();
